import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Tag, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import Table from "../../general/table-components/table";
import SkipAnalyticsContext from "../../../context/skip-analytics/context";
import { ISkipHistory } from "../../../models/skip-analytics/response";
import styles from "./styles.module.scss";

interface IProps {
  driverId?: number;
  title?: string;
}

const SkipHistoryTable: React.FC<IProps> = ({ driverId, title }) => {
  const { t } = useTranslation();
  const { skipHistory, historyQuery, loading, actions } =
    useContext(SkipAnalyticsContext);

  const columns: ColumnsType<ISkipHistory> = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: t("trip_id"),
      dataIndex: "tripId",
      key: "tripId",
      width: 80,
      render: (tripId) => (
        <a href={`/trips/details/${tripId}`} target="_blank" rel="noreferrer">
          {tripId}
        </a>
      ),
    },
    ...(driverId
      ? []
      : [
          {
            title: t("driver_name"),
            dataIndex: "driverName",
            key: "driverName",
            ellipsis: true,
          },
        ]),
    {
      title: t("skip_type"),
      dataIndex: "isStoreSkip",
      key: "isStoreSkip",
      width: 100,
      render: (isStore) =>
        isStore ? (
          <Tag color="blue">{t("store_skip")}</Tag>
        ) : (
          <Tag color="orange">{t("order_skip")}</Tag>
        ),
    },
    {
      title: t("skip_reason"),
      dataIndex: "skipReasonName",
      key: "skipReasonName",
      ellipsis: true,
    },
    {
      title: t("store"),
      dataIndex: "storeName",
      key: "storeName",
      ellipsis: true,
      render: (name) => name || "-",
    },
    {
      title: t("order"),
      dataIndex: "orderNumber",
      key: "orderNumber",
      width: 120,
      render: (orderNumber, record) =>
        orderNumber ? (
          <a
            href={`/orders/details/${record.orderId}`}
            target="_blank"
            rel="noreferrer"
          >
            {orderNumber}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("additional_note"),
      dataIndex: "additionalNote",
      key: "additionalNote",
      ellipsis: true,
      render: (note) =>
        note ? (
          <Tooltip title={note}>
            <span>{note}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: t("skipped_at"),
      dataIndex: "skippedAt",
      key: "skippedAt",
      width: 160,
      render: (date) =>
        date ? new Date(date).toLocaleString() : "-",
    },
  ];

  return (
    <Card className={styles.card} title={title || t("skip_history")}>
      <div className={styles.table}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={skipHistory?.data || []}
          loading={loading.includes("skipHistory")}
          scroll={{ x: 1200 }}
          pagination={{
            current: historyQuery?.page,
            pageSize: historyQuery?.per_page,
            total: skipHistory?.totalCount || 0,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t("of")} ${total}`,
            onChange: (page, pageSize) => {
              actions.setHistoryQuery({
                ...historyQuery,
                page,
                per_page: pageSize,
              });
            },
          }}
        />
      </div>
    </Card>
  );
};

export default SkipHistoryTable;
